/**
 * « Ton profil par phase » — sport et mental croisés avec les phases du cycle.
 *
 * Le widget est **toujours peuplé** : tant qu'une phase n'a pas assez de cycles observés,
 * elle porte la tendance générale plutôt qu'un vide. Un badge distingue explicitement
 * cette tendance de population du profil appris — sans quoi la sportive croit qu'on parle
 * d'elle alors qu'on affiche la moyenne des femmes.
 */
import React from 'react';

import { PHASE_COLORS_MAP } from '../constants/colors';
import './PhaseProfile.css';

const PHASE_SLUG = {
  REGLES: 'regles',
  FOLLICULAIRE: 'folliculaire',
  OVULATION: 'ovulation',
  LUTEALE: 'luteale',
};

const PHASE_LABEL = {
  REGLES: 'Règles',
  FOLLICULAIRE: 'Folliculaire',
  OVULATION: 'Ovulation',
  LUTEALE: 'Lutéale',
};

const TREND_LABEL = {
  favorable: 'plus favorable',
  defavorable: 'plus difficile',
  neutre: 'dans ta moyenne',
};

// Les clés de ressenti, matin et après-midi. `day_feeling` reste de la liste : il sert
// encore de repli côté serveur pour les relevés d'avant le découpage.
const DAY_FEELING_KEYS = new Set(['day_feeling', 'day_feeling_morning', 'day_feeling_afternoon']);

/** Le ressenti est stocké sur 0–2 ; les autres métriques se lisent telles quelles. */
const formatMean = (metric) => {
  if (metric.mean == null) return null;
  if (DAY_FEELING_KEYS.has(metric.key)) {
    return ['Mauvais', 'Normal', 'Bon'][Math.round(metric.mean)] ?? '—';
  }
  if (metric.key === 'rest_heart_rate') return `${Math.round(metric.mean)} bpm`;
  return `${metric.mean.toFixed(1)}/10`;
};

const PhaseProfile = ({ insights = [], domain }) => {
  if (!insights.length) return null;

  return (
    <div className="phase-groups">
      {insights.map((phase) => {
        const slug = PHASE_SLUG[phase.phase] ?? 'regles';
        const color = PHASE_COLORS_MAP[slug];
        const personal = phase.source === 'personnelle';
        // Chaque page ne montre que son domaine : la page Sport parle RPE et FC, la page
        // Mental parle concentration et ressenti.
        const metrics = phase.metrics.filter((m) => m.domain === domain && m.mean != null);
        const tendency = domain === 'sport' ? phase.tendance_sport : phase.tendance_mental;

        return (
          <div key={phase.phase} className="phase-profile__card phase-bar" style={{ '--phase-bar-color': color }}>
            <div className="phase-profile__head">
              <span className="phase-group-label" style={{ color }}>
                {PHASE_LABEL[phase.phase] ?? phase.phase}
              </span>
              <span
                className={`phase-profile__badge${personal ? ' phase-profile__badge--personal' : ''}`}
              >
                {personal ? 'ton profil' : 'tendance générale'}
              </span>
            </div>

            {personal && metrics.length > 0 ? (
              <>
                <div className="phase-profile__metrics">
                  {metrics.map((m) => (
                    <div key={m.key} className="phase-profile__metric">
                      <span className="phase-profile__metric-label">{m.label}</span>
                      <span className="phase-profile__metric-value">{formatMean(m)}</span>
                    </div>
                  ))}
                </div>
                {metrics.some((m) => m.ranges.length > 0) && (
                  <ul className="phase-profile__ranges">
                    {metrics.flatMap((m) =>
                      m.ranges.map((r) => (
                        <li key={`${m.key}-${r.start_day}`}>
                          <strong>J{r.start_day}–J{r.end_day}</strong> · {m.label}{' '}
                          {TREND_LABEL[r.trend] ?? r.trend}
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </>
            ) : (
              <>
                <p className="phase-profile__tendency">{tendency}</p>
                {phase.cycles_missing > 0 && (
                  <p className="phase-profile__progress">
                    Encore {phase.cycles_missing} cycle{phase.cycles_missing > 1 ? 's' : ''} pour
                    ton profil personnel
                  </p>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PhaseProfile;
